import { TFunction } from 'i18next'
import * as yup from 'yup'

export const PASSWORD_RULES = {
  MIN_LENGTH: 6,
  MAX_LENGTH: 100,
  ONLY_LETTERS_AND_NUMBERS_REGEX: /^[A-Za-z0-9]+$/,
  MUST_HAVE_LETTER_AND_NUMBER_REGEX: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]+$/,
} as const

export interface PasswordValidationErrors {
  newPassword?: string
  newPasswordConfirmation?: string
}

export const validatePassword = (
  values: { newPassword: string; newPasswordConfirmation: string },
  t: (key: string, options?: { number?: number }) => string
): PasswordValidationErrors => {
  const errors: PasswordValidationErrors = {}

  if (!values.newPassword) {
    errors.newPassword = t('formMessages:REQUIRED')
  } else if (values.newPassword.length < PASSWORD_RULES.MIN_LENGTH) {
    errors.newPassword = t('formMessages:SIZE_MINIMUM', {
      number: PASSWORD_RULES.MIN_LENGTH,
    })
  } else if (values.newPassword.length > PASSWORD_RULES.MAX_LENGTH) {
    errors.newPassword = t('formMessages:SIZE_MAXIMUM', {
      number: PASSWORD_RULES.MAX_LENGTH,
    })
  } else if (
    !PASSWORD_RULES.ONLY_LETTERS_AND_NUMBERS_REGEX.test(values.newPassword)
  ) {
    errors.newPassword = t('formMessages:RESET_PASSWORD_LETTER_NUMBER_REQUIRED')
  } else if (
    !PASSWORD_RULES.MUST_HAVE_LETTER_AND_NUMBER_REGEX.test(values.newPassword)
  ) {
    errors.newPassword = t('formMessages:RESET_PASSWORD_LETTER_NUMBER_REQUIRED')
  }

  if (!values.newPasswordConfirmation) {
    errors.newPasswordConfirmation = t('formMessages:REQUIRED')
  }

  if (values.newPassword !== values.newPasswordConfirmation) {
    errors.newPasswordConfirmation = t('formMessages:PASSWORD_MATCH')
  }

  return errors
}

export const getPasswordValidationSchema = (t: TFunction) => {
  return yup
    .string()
    .required(t('formMessages:REQUIRED'))
    .min(
      PASSWORD_RULES.MIN_LENGTH,
      t('formMessages:SIZE_MINIMUM', { number: PASSWORD_RULES.MIN_LENGTH })
    )
    .matches(
      PASSWORD_RULES.ONLY_LETTERS_AND_NUMBERS_REGEX,
      t('formMessages:RESET_PASSWORD_LETTER_NUMBER_REQUIRED')
    )
    .matches(
      PASSWORD_RULES.MUST_HAVE_LETTER_AND_NUMBER_REGEX,
      t('formMessages:RESET_PASSWORD_LETTER_NUMBER_REQUIRED')
    )
}

export const getSignUpValidationSchema = (t: TFunction) => {
  return yup.object().shape({
    firstName: yup.string().required(t('formMessages:REQUIRED')),
    lastName: yup.string().required(t('formMessages:REQUIRED')),
    email: yup
      .string()
      .email(t('formMessages:EMAIL'))
      .required(t('formMessages:REQUIRED')),
    phones: yup.string().required(t('formMessages:REQUIRED')),
    password: getPasswordValidationSchema(t),
    newsletter: yup.boolean(),
    conditions: yup.boolean().required(t('formMessages:REQUIRED')),
  })
}
